import type { DateTime } from "luxon";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { showAgencyPostingResult } from "../../../utils/agencyWarnings";
import { type AgencyPostingFailure, AgencySource, ChangesSource, type OpenShiftRequest, type ShiftRequest } from "../../../utils/models";
import axiosInstance from "../../../utils/networkWrapper";
import { isOpenShiftDurationAllowed } from "../../../utils/openShiftDuration";
import { getOverlappingShift, isAgencyPostedShift } from "../../../utils/utilFunctions";
import { showConfirmationDialog } from "../../staffingSearch/CreateShiftContent";
import { useMergeRequests } from "./useMergeRequests";

/**
 * What an hours edit did, resolved by `handleRequestTimeChange` once its writes and its requests refresh are done.
 * Existing callers ignore it; the gaps posted sheet reads it to tell a save from a merge from a cancelled prompt from a
 * failure, rather than off the requests list, whose refresh has not committed when the awaited call returns.
 */
export enum RequestTimeChangeOutcome {
	/** The request's hours were written. */
	Saved = "saved",
	/** The overlap prompt was answered "Merge": the request was folded into an identical one. */
	Merged = "merged",
	/** A prompt (the overlap merge, the Nursa re-post warning) was cancelled: nothing was written. */
	Cancelled = "cancelled",
	/** Refused or failed — already shown in the error snackbar. */
	Failed = "failed",
}

export const useUpdateRequestTime = () => {
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const { setStaffRequests, refreshRequests, staffRequests } = useRequestsContext();
	const { trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { mergeRequests } = useMergeRequests();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { t } = useTranslation();

	/** The hours edit, resolving what it did. `handleRequestTimeChange` below is the same call for callers that ignore it. */
	const updateRequestTime = useCallback(
		async (request: ShiftRequest, newStartTime: DateTime, newEndTime: DateTime, source: ChangesSource): Promise<RequestTimeChangeOutcome> => {
			try {
				if (!isOpenShiftDurationAllowed(newStartTime, newEndTime)) {
					showSnackbar(t("error.open.shift.duration.too.long"), "error");
					return RequestTimeChangeOutcome.Failed;
				}
				// Time validation for requests with responses
				const hasResponses = request.message.request_data.some(({ nurse_id }) => !!nurse_id);

				// Time validation for requests with responses
				if (hasResponses) {
					const oldStartTime = request.message.shift_start;
					const oldEndTime = request.message.shift_end;

					if (newStartTime < oldStartTime || newEndTime > oldEndTime) {
						throw new Error(
							newStartTime < oldStartTime
								? "New start time can't be before the old start time for requests with responses"
								: "New end time can't be after the old end time for requests with responses",
						);
					}
				}

				// Check for overlapping shifts — match by this request's actual snapshot windows.
				const existingMatchingRequest = getOverlappingShift({
					staffRequests,
					startTime: newStartTime,
					endTime: newEndTime,
					roles: request.message.roles,
					jobManagementEnabled,
					jobId: request.job_id,
					intendedBonusWindows: request.incentive_windows ?? [],
					chargeOnly: request.charge_only ?? false,
					location: request.location,
					postToAgency: isAgencyPostedShift(request),
					subRole: request.sub_role,
					agencySourceName: (request as OpenShiftRequest).agency_source_name,
				});

				const hasOverlap = existingMatchingRequest && existingMatchingRequest.id !== request.id;

				if (hasOverlap) {
					// Handle merge case (for both with/without responses)
					const oldNeededNurses = (existingMatchingRequest as any).needed_nurses;
					const newNeededNurses = oldNeededNurses + ((request as any).needed_nurses ?? 1);

					const userChoice = await new Promise<"merge" | "cancel">((resolve) => {
						showConfirmationDialog({
							title: "Overlapping Open Shift",
							content: "A similar open shift already exists. Would you like to merge the shifts?",
							actions: [
								{
									label: "Cancel",
									onClick: () => resolve("cancel"),
								},
								{
									label: `Merge (${newNeededNurses})`,
									onClick: () => resolve("merge"),
								},
							],
						});
					});

					if (userChoice === "merge") {
						await mergeRequests({
							request,
							matching: existingMatchingRequest as OpenShiftRequest,
							newNeededNurses,
							source,
							successMessageKey: "successfully.merged.requests",
						});
						// Reflect the time change locally too (the merge itself doesn't change times).
						request.message.shift_start = newStartTime;
						request.message.shift_end = newEndTime;
						return RequestTimeChangeOutcome.Merged;
					}
					return RequestTimeChangeOutcome.Cancelled;
				}

				// A Nursa-posted shift re-posts (cancel + new post) on an hours edit, which resets any
				// nurses' existing pickup requests — warn the scheduler before proceeding.
				const isNursaPosted = (request as OpenShiftRequest).agency_source_name === AgencySource.Nursa;
				if (isNursaPosted && hasResponses) {
					const choice = await new Promise<"continue" | "cancel">((resolve) => {
						showConfirmationDialog({
							title: t("edit.nursa.shift.pickups.warning.title"),
							content: t("edit.nursa.shift.pickups.warning"),
							actions: [
								{ label: t("cancel"), onClick: () => resolve("cancel") },
								{ label: t("continue"), onClick: () => resolve("continue") },
							],
						});
					});
					if (choice === "cancel") return RequestTimeChangeOutcome.Cancelled;
				}

				// Update existing request times
				const response = await axiosInstance.post<{ message: string; agency_posting_failures?: AgencyPostingFailure[] }>(
					`/staffing/update-request-posted-shift-times/${request.id}`,
					{
						roles: request.message.roles,
						needed_nurses: (request as any).needed_nurses,
						shift_type: request.message.shift_type,
						shift_start: newStartTime,
						shift_end: newEndTime,
					},
					{
						params: {
							department_id: chosenDepartment.department_id,
							source: source,
						},
					},
				);

				// Update local state
				request.message.shift_start = newStartTime;
				request.message.shift_end = newEndTime;

				await refreshRequests();
				showAgencyPostingResult(showSnackbar, t("posted.shift.times.updated.successfully"), t, response.data.agency_posting_failures);
				return RequestTimeChangeOutcome.Saved;
			} catch (error) {
				trackError("Failed to update request time", {
					request_id: request.id,
					new_start_time: newStartTime.toISO(),
					new_end_time: newEndTime.toISO(),
					error: error as string,
				});
				showErrorSnackbar(
					(error as Error).message || "Failed to update posted shift times",
					{
						action: "day view time edit failed",
						source: ChangesSource.DaySchedule,
					},
					error,
				);
				return RequestTimeChangeOutcome.Failed;
			}
		},
		[chosenDepartment.department_id, setStaffRequests, refreshRequests, trackError, staffRequests, showErrorSnackbar, showSnackbar, t],
	);

	const handleRequestTimeChange = useCallback(
		async (request: ShiftRequest, newStartTime: DateTime, newEndTime: DateTime, source: ChangesSource): Promise<void> => {
			await updateRequestTime(request, newStartTime, newEndTime, source);
		},
		[updateRequestTime],
	);

	return { handleRequestTimeChange, updateRequestTime };
};
